import { Constants } from '../shared/Constants';
import { AjaxMappingBuilder } from './AjaxMappingBuilder';

let isErrorModalActive = false;
let activeModalTimestamp = 0;

export function formatErrorMessage(xhr, status, err) {
    if (xhr && xhr.responseJSON) {
        const json = xhr.responseJSON;
        if (json.message) return json.message;
        if (json.error) return typeof json.error === 'string' ? json.error : (json.error.message || JSON.stringify(json.error));
        if (json.Detail) return json.Detail;
    }
    if (xhr && xhr.responseText && typeof xhr.responseText === 'string' && xhr.responseText.length < 200) {
        try {
            const parsed = JSON.parse(xhr.responseText);
            if (parsed.message) return parsed.message;
            if (parsed.error) return typeof parsed.error === 'string' ? parsed.error : parsed.error.message;
        } catch (_) {
            // Not JSON
        }
    }
    if (status === 'parsererror') {
        return 'Invalid JSON response received from the server.';
    }
    if (xhr && xhr.status && xhr.status >= 400) {
        const statusText = xhr.statusText ? ` (${xhr.statusText})` : '';
        return `Request failed with HTTP status ${xhr.status}${statusText}.`;
    }
    if (xhr && xhr.status === 0) {
        return 'Network error or request timed out.';
    }
    if (status === 'timeout') {
        return 'The request timed out. Please check your network connection and try again.';
    }
    if (typeof err === 'string' && err.trim().length > 0) {
        return err;
    }
    if (err && err.message) {
        return err.message;
    }
    return 'An unexpected error occurred while loading table data.';
}

export function showApiErrorModal(xhr, status, err, userErrorCallback, settings, errorTitleConfig, errorMessageConfig) {
    const now = Date.now();
    if (isErrorModalActive) {
        if (now - activeModalTimestamp < 500) {
            return;
        }
        isErrorModalActive = false;
    }

    if (typeof window !== 'undefined' && window.jQuery) {
        const $ = window.jQuery;
        const $table = (settings && settings.nTable) ? $(settings.nTable) : $('.dt-core, .table, table');
        if ($table && $table.length) {
            $table.closest('.dataTables_wrapper, .dt-core-wrapper').find('.dataTables_processing, .dt-processing, [class*="processing"]').hide().css('display', 'none');
            $table.each(function () {
                const t = $(this);
                if ($.fn.DataTable && $.fn.DataTable.isDataTable(t)) {
                    try {
                        const api = t.DataTable();
                        if (typeof api.processing === 'function') api.processing(false);
                        api.clear().draw(false);
                    } catch (_) {}
                }
                const $emptyCell = t.find('tbody td.dataTables_empty');
                if ($emptyCell.length > 0 && ($emptyCell.text().trim() === 'Loading...' || $emptyCell.text().trim() === '')) {
                    $emptyCell.text('No data available in table');
                }
            });
        }
        $('.dataTables_processing, .dt-processing, [class*="processing"]').hide().css('display', 'none');
    }

    let customMsg = null;
    if (typeof userErrorCallback === 'function') {
        try {
            const result = userErrorCallback(xhr, status, err);
            if (typeof result === 'string' && result.trim().length > 0) {
                customMsg = result;
            }
        } catch (e) {
            console.error('[DataTableWrapper] User onError handler threw an error:', e);
        }
    }

    if (!customMsg && errorMessageConfig) {
        customMsg = typeof errorMessageConfig === 'function' ? errorMessageConfig(xhr, status, err) : errorMessageConfig;
    }

    if (typeof document !== 'undefined' && document.querySelector('.dt-api-error-modal')) {
        return;
    }

    const message = customMsg || formatErrorMessage(xhr, status, err);
    let title = 'API Request Error';
    if (errorTitleConfig) {
        title = typeof errorTitleConfig === 'function' ? errorTitleConfig(xhr, status, err) : errorTitleConfig;
    }

    if (typeof window !== 'undefined' && window.GenericModal && typeof window.GenericModal.create === 'function') {
        isErrorModalActive = true;
        activeModalTimestamp = now;

        const builder = window.GenericModal.create()
            .asError(title)
            .withBody(message);

        if (typeof builder.onHidden === 'function') {
            builder.onHidden(() => {
                isErrorModalActive = false;
            });
        }

        const modal = builder.show();
        if (modal && modal._element) {
            modal._element.classList.add('dt-api-error-modal');
        }
    } else {
        console.error('[DataTableWrapper] API Error:', message);
    }
}

export class AjaxBuilder {
    constructor() {
        this._config = {
            url: '',
            method: Constants.Http.GET,
            requestData: null,
            beforeSend: null,
            beforeRequest: null,
            complete: null,
            error: null,
            errorTitle: null,
            errorMessage: null,
            mapping: null,
            transform: null
        };
    }

    withUrl(url) { this._config.url = url; return this; }
    withMethod(method) { this._config.method = method; return this; }
    withRequestData(dataOrFn) { this._config.requestData = dataOrFn; return this; }
    onBeforeSend(fn) { this._config.beforeSend = fn; return this; }
    onBeforeRequest(fn) { this._config.beforeRequest = fn; return this; }
    onComplete(fn) { this._config.complete = fn; return this; }
    onError(fn) { this._config.error = fn; return this; }
    withErrorTitle(titleOrFn) { this._config.errorTitle = titleOrFn; return this; }
    withErrorMessage(messageOrFn) { this._config.errorMessage = messageOrFn; return this; }

    withMapping(mappingFn) {
        const builder = new AjaxMappingBuilder();
        mappingFn(builder);
        this._config.mapping = builder;
        return this;
    }

    transform(fn) {
        this._config.transform = fn;
        return this;
    }

    _normalizePayload(json) {
        // DataTables usually gives an object, but APIs sometimes respond with a JSON string.
        let payload = json;
        if (typeof payload === 'string') {
            try {
                payload = JSON.parse(payload);
            } catch (e) {
                console.error('[DataTableWrapper] Invalid JSON response (string).', e);
                showApiErrorModal(null, 'parsererror', e, this._config.error, null, this._config.errorTitle, this._config.errorMessage);
                return [];
            }
        }

        // Guard: if payload isn't an object/array, return empty dataset.
        if (payload == null || (typeof payload !== 'object' && !Array.isArray(payload))) {
            return [];
        }

        // If mapping is configured, resolve into the DataTables server-side shape.
        if (this._config.mapping && payload && typeof payload === 'object' && !Array.isArray(payload)) {
            const mapped = this._config.mapping._resolve(payload);
            payload.recordsTotal = mapped.recordsTotal;
            payload.recordsFiltered = mapped.recordsFiltered;
            payload.data = mapped.data;
        }

        // Normalize common API shapes into `data`.
        let dataArray =
            (payload && typeof payload === 'object' && !Array.isArray(payload) && (payload.data ?? payload.Data ?? payload.items ?? payload.Items ?? payload.results ?? payload.Results)) ||
            (Array.isArray(payload) ? payload : []);

        if (!Array.isArray(dataArray)) dataArray = [];

        if (this._config.transform) {
            dataArray = dataArray.map(item => this._config.transform(item));
        }

        // Mutate the original reference when possible (DT uses it downstream).
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            payload.data = dataArray;
            if (payload.recordsTotal == null) payload.recordsTotal = dataArray.length;
            if (payload.recordsFiltered == null) payload.recordsFiltered = dataArray.length;
        }

        return payload;
    }

    _build() {
        // Must return a jqXHR-like object so DataTables can call abort() on reload.
        // An async function returns a Promise (no abort) and crashes DT's __reload.
        return (d, callback, settings) => {
            let aborted = false;
            let xhr = null;

            const handle = {
                get readyState() {
                    if (xhr) return xhr.readyState;
                    return aborted ? 4 : 1;
                },
                abort() {
                    aborted = true;
                    if (xhr && typeof xhr.abort === 'function') {
                        xhr.abort();
                    }
                }
            };

            const emptyPayload = () => ({
                draw: d?.draw,
                data: [],
                recordsTotal: 0,
                recordsFiltered: 0
            });

            const startAjax = () => {
                if (aborted) return;

                xhr = $.ajax({
                    url: this._config.url,
                    type: this._config.method,
                    data: typeof this._config.requestData === 'function'
                        ? this._config.requestData(d, settings)
                        : this._config.requestData ?? d,
                    beforeSend: this._config.beforeSend,
                    complete: this._config.complete,
                    error: (req, status, err) => {
                        if (aborted || status === 'abort') return;
                        showApiErrorModal(req, status, err, this._config.error, settings, this._config.errorTitle, this._config.errorMessage);
                        callback(emptyPayload());
                    }
                }).done((json) => {
                    if (aborted) return;
                    try {
                        const payload = this._normalizePayload(json);
                        if (Array.isArray(payload)) {
                            callback({
                                draw: d?.draw,
                                data: payload,
                                recordsTotal: payload.length,
                                recordsFiltered: payload.length
                            });
                            return;
                        }
                        callback({
                            draw: d?.draw,
                            ...payload
                        });
                    } catch (e) {
                        console.error('[DataTableWrapper] Failed to normalize ajax response.', e);
                        showApiErrorModal(null, 'parsererror', e, this._config.error, settings, this._config.errorTitle, this._config.errorMessage);
                        callback(emptyPayload());
                    }
                });
            };

            if (this._config.beforeRequest) {
                Promise.resolve(this._config.beforeRequest({ requestData: d, settings }))
                    .then((gateResult) => {
                        if (aborted) return;
                        if (gateResult === false) {
                            callback(emptyPayload());
                            return;
                        }
                        startAjax();
                    })
                    .catch((e) => {
                        console.error('[DataTableWrapper] beforeRequest failed.', e);
                        if (!aborted) callback(emptyPayload());
                    });
            } else {
                startAjax();
            }

            return handle;
        };
    }
}

